// AppDialog — the standard modal wrapper for the app.
//
// This component centralises every UX rule we want to be true for *every*
// modal in the product:
//
//   • Header: title (optional icon, subtitle, header chip), back-arrow for
//     multi-step flows, and a top-right close X. The X auto-disables when
//     `busy` is true so users can't dismiss a save in flight.
//   • Mobile: full-screen below `sm` by default — no awkward floating cards
//     on phones. Opt out with `disableMobileFullscreen`.
//   • Esc / backdrop: click-outside and Esc are blocked while `busy`.
//   • Footer: pass either a custom `actions` slot, or use the convenience
//     `primaryAction` / `secondaryAction` props which render a consistent
//     `LoadingButton` pair with the right padding, ordering, and busy state.
//   • Theming: inherits Paper radius/shadow/border from the global theme
//     overrides — so pages no longer need to set `PaperProps={{ borderRadius
//     ... }}` ad-hoc.
//
// Goal: every page-level Dialog in the app should look interchangeable with
// every other one, without each author having to remember twelve detail
// rules.

import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Stack,
  Tooltip,
  Button,
  CircularProgress,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseRounded from '@mui/icons-material/CloseRounded';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';

export type AppDialogSize = 'xs' | 'sm' | 'md' | 'lg';

export interface AppDialogActionProps {
  label: React.ReactNode;
  onClick?: () => void | Promise<void>;
  /** Show a spinner inside the button and lock it. */
  loading?: boolean;
  /** Disable independent of loading (e.g. validation failure). */
  disabled?: boolean;
  /** Standard MUI button color. Use `error` for destructive primary actions. */
  color?: 'primary' | 'error' | 'warning' | 'success' | 'info' | 'inherit';
  /** Optional leading icon. Hidden while loading. */
  icon?: React.ReactNode;
  /** Useful for form submission. */
  type?: 'button' | 'submit';
  /** Tooltip shown on hover (e.g. why an action is disabled). */
  tooltip?: string;
  /** Use this as the dialog's autofocus target. */
  autoFocus?: boolean;
}

export type AppDialogCloseReason = 'backdropClick' | 'escapeKeyDown' | 'closeButton' | 'backButton';

export interface AppDialogProps {
  open: boolean;
  /**
   * Called when the user dismisses (Esc, backdrop, the close X, or the back arrow).
   * Mirrors MUI's Dialog signature so existing handlers — including narrowly
   * typed `(event, reason)` callbacks pulled from MUI examples — fit unchanged.
   * Declared as a method shape so TypeScript checks it bivariantly and accepts
   * both `() => void` and `(event, reason) => void` style handlers.
   */
  onClose(event?: object, reason?: AppDialogCloseReason): void;

  // ── Header ────────────────────────────────────────────────────────────
  /** Title text or rich node (will be wrapped in our standard Typography). */
  title: React.ReactNode;
  /** Optional one-line subtitle below the title. Keep it short. */
  subtitle?: React.ReactNode;
  /** Optional leading icon next to the title. */
  icon?: React.ReactNode;
  /** Extra header content (e.g. a Chip). Sits between the title and the close X. */
  headerExtras?: React.ReactNode;
  /** Show a back-arrow before the title (for multi-step flows). */
  onBack?: () => void;
  /** Hide the top-right close X (rare). */
  hideCloseButton?: boolean;

  // ── Behavior ──────────────────────────────────────────────────────────
  /**
   * When true, locks the modal: backdrop/Esc do nothing, the close X is
   * disabled, secondary action is disabled, and the primary action's
   * `loading` flag is forced on (if present). Use this during async
   * submits so users can't lose their work.
   */
  busy?: boolean;
  /** Width preset. Maps to MUI maxWidth + a sensible Paper minHeight. */
  size?: AppDialogSize;
  /** Opt out of mobile full-screen behavior. */
  disableMobileFullscreen?: boolean;

  // ── Content ───────────────────────────────────────────────────────────
  children: React.ReactNode;
  /** Override DialogContent sx (rare). */
  contentSx?: object;
  /** Show the standard horizontal divider between header/content. */
  dividers?: boolean;

  // ── Footer ────────────────────────────────────────────────────────────
  /** Custom action area. Mutually exclusive with primaryAction/secondaryAction. */
  actions?: React.ReactNode;
  /** Convenience primary action — renders right side, contained variant. */
  primaryAction?: AppDialogActionProps;
  /** Convenience secondary action — renders left side, text variant. Defaults to "Cancel" + onClose if not provided when primaryAction is present. */
  secondaryAction?: AppDialogActionProps;
  /**
   * Extra content rendered on the LEFT side of the action bar (e.g. a
   * destructive tertiary action, or a help link). Pushed all the way left,
   * with the cancel/primary buttons clinging to the right.
   */
  actionsLeftExtra?: React.ReactNode;

  // ── Pass-through ──────────────────────────────────────────────────────
  ariaLabelledBy?: string;
  ariaDescribedBy?: string;
  /** Override Paper sx. Use sparingly — prefer the standard. */
  paperSx?: object;
  /** Optional className on the root Dialog (rare; for portal-targeted styles). */
  className?: string;
}

/** Map our size preset → MUI maxWidth + a sensible minHeight */
const SIZE_MAP: Record<AppDialogSize, { maxWidth: 'xs' | 'sm' | 'md' | 'lg'; minHeight?: number }> = {
  xs: { maxWidth: 'xs' },
  sm: { maxWidth: 'sm' },
  md: { maxWidth: 'md' },
  lg: { maxWidth: 'lg' },
};

/**
 * Render a button with consistent loading semantics. We don't use
 * `@mui/lab` LoadingButton here because the app already has three
 * different loading patterns and keeping it native MUI keeps the
 * dependency surface small.
 */
function ActionButton({
  variant,
  action,
  forceLoading,
}: {
  variant: 'contained' | 'text' | 'outlined';
  action: AppDialogActionProps;
  /** When true, force loading state regardless of the action's own flag. */
  forceLoading?: boolean;
}) {
  const loading = !!(action.loading || forceLoading);
  const disabled = !!action.disabled || loading;
  const button = (
    <Button
      variant={variant}
      color={action.color || 'primary'}
      onClick={action.onClick}
      disabled={disabled}
      type={action.type || 'button'}
      autoFocus={action.autoFocus}
      startIcon={loading ? <CircularProgress size={14} color="inherit" /> : action.icon}
      sx={{ minWidth: 96, textTransform: 'none', fontWeight: 600 }}
    >
      {action.label}
    </Button>
  );
  return action.tooltip ? (
    <Tooltip title={action.tooltip} disableInteractive>
      <span>{button}</span>
    </Tooltip>
  ) : button;
}

export default function AppDialog({
  open,
  onClose,
  title,
  subtitle,
  icon,
  headerExtras,
  onBack,
  hideCloseButton,
  busy,
  size = 'sm',
  disableMobileFullscreen,
  children,
  contentSx,
  dividers,
  actions,
  primaryAction,
  secondaryAction,
  actionsLeftExtra,
  ariaLabelledBy,
  ariaDescribedBy,
  paperSx,
  className,
}: AppDialogProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const fullScreen = !disableMobileFullscreen && isMobile;
  const sizeCfg = SIZE_MAP[size];
  const labelledBy = ariaLabelledBy || 'app-dialog-title';

  // Wrapper that swallows close attempts while busy. We do this here, once,
  // so individual modals don't have to remember `onClose={busy ? undefined : onClose}`.
  const handleClose = (e?: object, reason?: AppDialogCloseReason) => {
    if (busy) return;
    onClose(e, reason);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={sizeCfg.maxWidth}
      fullWidth
      fullScreen={fullScreen}
      className={className}
      aria-labelledby={labelledBy}
      aria-describedby={ariaDescribedBy}
      PaperProps={{
        sx: {
          // Theme already applies radius/shadow/border. We only set per-instance
          // structural rules here.
          display: 'flex',
          flexDirection: 'column',
          ...(fullScreen
            ? { borderRadius: 0, m: 0, maxHeight: '100%' }
            : { maxHeight: 'min(92vh, 880px)' }),
          ...(paperSx || {}),
        },
      }}
    >
      {/* ── Header ────────────────────────────────────────────────────── */}
      <Box
        sx={(t) => ({
          flexShrink: 0,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          px: { xs: 2, sm: 2.75 },
          pt: 2.25,
          pb: subtitle ? 1.25 : 1.75,
          borderBottom: dividers ? `1px solid ${t.tokens.border.subtle}` : 'none',
        })}
      >
        {onBack && (
          <Tooltip title="Back">
            <span>
              <IconButton
                size="small"
                onClick={onBack}
                disabled={busy}
                aria-label="Back"
                sx={{ ml: -0.5, mt: 0.25 }}
              >
                <ArrowBackRounded sx={{ fontSize: 18 }} />
              </IconButton>
            </span>
          </Tooltip>
        )}

        {icon && (
          <Box
            sx={{
              flexShrink: 0,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              mt: 0.25,
              '& svg': { fontSize: 22 },
            }}
          >
            {icon}
          </Box>
        )}

        <Box sx={{ flex: 1, minWidth: 0 }}>
          {typeof title === 'string' ? (
            <Typography
              id={labelledBy}
              sx={{
                fontWeight: 700,
                fontSize: '1.0625rem',
                lineHeight: 1.3,
                letterSpacing: '-0.005em',
              }}
            >
              {title}
            </Typography>
          ) : (
            <Box id={labelledBy}>{title}</Box>
          )}
          {subtitle && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5, fontSize: '0.8125rem', lineHeight: 1.5 }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>

        {headerExtras && (
          <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
            {headerExtras}
          </Box>
        )}

        {!hideCloseButton && (
          <Tooltip title="Close">
            <span>
              <IconButton
                size="small"
                onClick={(e) => handleClose(e, 'closeButton')}
                disabled={busy}
                aria-label="Close"
                sx={{ flexShrink: 0, color: 'text.secondary' }}
              >
                <CloseRounded sx={{ fontSize: 20 }} />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>

      {/* ── Content ───────────────────────────────────────────────────── */}
      <DialogContent
        dividers={dividers}
        sx={{
          flex: '1 1 auto',
          minHeight: 0,
          // Theme sets px=22; we keep that and add a tasteful bottom buffer
          // so content doesn't crash into the action bar.
          py: 2,
          ...(contentSx || {}),
        }}
      >
        {children}
      </DialogContent>

      {/* ── Footer ────────────────────────────────────────────────────── */}
      {(actions || primaryAction || secondaryAction || actionsLeftExtra) && (
        <DialogActions
          sx={{
            flexShrink: 0,
            px: { xs: 2, sm: 2.75 },
            py: 1.5,
            gap: 1,
            borderTop: (t) => `1px solid ${t.tokens.border.subtle}`,
            // On mobile we stack so primary stays on top — that's the iOS
            // convention and avoids action-bar overflow.
            flexDirection: { xs: 'column-reverse', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            '& > .actions-left': {
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              minWidth: 0,
            },
            '& .MuiButton-root': {
              minWidth: { xs: 'unset', sm: 96 },
            },
          }}
        >
          {actionsLeftExtra && <Box className="actions-left">{actionsLeftExtra}</Box>}

          {actions ? (
            actions
          ) : (
            <>
              <ActionButton
                variant="text"
                action={
                  secondaryAction || {
                    label: 'Cancel',
                    onClick: onClose,
                    color: 'inherit',
                    disabled: busy,
                  }
                }
              />
              {primaryAction && (
                <ActionButton
                  variant="contained"
                  action={primaryAction}
                  forceLoading={busy}
                />
              )}
            </>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
}
